import { useUserStore } from "@/modules/auth/store/user";
import { formatNumber } from "@/modules/auth/utils/format";
import { constants } from "@/modules/calculations/consts";
import { useTranslation } from "react-i18next";

const getFontSizeByLength = (length: number, maxFont = 24, minFont = 12) => {
  return length <= 5 ? maxFont : Math.max(minFont, maxFont - (length - 5) * 2);
};

const Statistics = () => {
  const { t } = useTranslation();

  const { deposits } = useUserStore();

  // TOTAL DEPOSITED
  const totalTokenAmount = deposits.reduce((sum, d) => {
    const amount = d.deposit.tokenAmount;
    return sum + amount;
  }, 0);
  const totalDeposited = formatNumber(totalTokenAmount, {
    precision: totalTokenAmount < 100 ? constants.TOKEN_DECIMALS : 2,
  });

  // TOTAL DEPOSITED USD AMOUNT
  const totalUSDTokenAmount = deposits.reduce((sum, d) => {
    const amount = d.deposit.usdAmount;
    return sum + amount;
  }, 0);
  const totalDepositedUSD = formatNumber(totalUSDTokenAmount, {
    precision: 2,
  });

  // TOTAL EARNED
  const totalTokenAmountEarned = deposits.reduce((sum, d) => {
    const amount = d.reward.targetDateTotal.amount;
    return sum + amount;
  }, 0);
  const totalEarned = formatNumber(totalTokenAmountEarned, {
    precision: totalTokenAmountEarned < 100 ? constants.TOKEN_DECIMALS : 2,
  });

  // TOTAL WITHDRAWN
  const totalTokenAmountWithdrawn = deposits.reduce((sum, d) => {
    const amount = d.reward.withdrawnTokenAmount;
    return sum + amount;
  }, 0);
  const totalWithdrawn = formatNumber(totalTokenAmountWithdrawn, {
    precision: totalTokenAmountWithdrawn < 100 ? constants.TOKEN_DECIMALS : 2,
  });

  const depositedFontSize = getFontSizeByLength(totalDeposited.length);
  const depositedUSDFontSize = getFontSizeByLength(totalDepositedUSD.length);
  const earnedFontSize = getFontSizeByLength(totalEarned.length);
  const withdrawnFontSize = getFontSizeByLength(totalWithdrawn.length);

  return (
    <div className="w-full flex flex-col">
      <p className="p-4 pb-2 text-[0.8125rem] leading-[1rem] text-section-header-text tracking-tighter uppercase">
        {t("mydeposits-page.statistics")}
      </p>

      <div className="w-full flex flex-col gap-2">
        <div className="w-full flex items-center justify-between gap-4 px-4 bg-bg rounded-[0.75rem]">
          <div className="flex-1 shrink-0 flex flex-col py-3 gap-0.5">
            <div className="flex items-center gap-1">
              <p
                className="leading-[1.75rem] font-semibold text-text"
                style={{ fontSize: `${depositedFontSize}px` }}
              >
                {totalDeposited}
              </p>
              <p className="text-[1.0625rem] leading-[1.375rem] text-hint">
                BTN
              </p>
            </div>
            <p className="text-[0.9375rem] leading-[1.25rem] text-subtitle-text">
              {t("mydeposits-page.total_deposited")}
            </p>
          </div>
        </div>

        <div className="w-full flex items-center justify-between gap-4 px-4 bg-bg rounded-[0.75rem]">
          <div className="flex-1 shrink-0 flex flex-col py-3 gap-0.5">
            <div className="flex items-center gap-1">
              <p
                className="leading-[1.75rem] font-semibold text-text"
                style={{ fontSize: `${depositedUSDFontSize}px` }}
              >
                {totalDepositedUSD}
              </p>
              <p className="text-[1.0625rem] leading-[1.375rem] text-hint">
                USD
              </p>
            </div>
            <p className="text-[0.9375rem] leading-[1.25rem] text-subtitle-text">
              {t("mydeposits-page.total_amount")}
            </p>
          </div>
        </div>

        <div className="w-full flex items-center justify-between gap-4 px-4 bg-bg rounded-[0.75rem]">
          <div className="flex-1 shrink-0 flex flex-col py-3 gap-0.5">
            <div className="flex items-center gap-1">
              <p
                className="leading-[1.75rem] font-semibold text-text"
                style={{ fontSize: `${earnedFontSize}px` }}
              >
                {totalEarned}
              </p>
              <p className="text-[1.0625rem] leading-[1.375rem] text-hint">
                BTN
              </p>
            </div>
            <p className="text-[0.9375rem] leading-[1.25rem] text-subtitle-text">
              {t("mydeposits-page.total_earned")}
            </p>
          </div>

          <div className="flex-1 shrink-0 flex flex-col py-3 gap-0.5">
            <div className="flex items-center gap-1">
              <p
                className="leading-[1.75rem] font-semibold text-text"
                style={{ fontSize: `${withdrawnFontSize}px` }}
              >
                {totalWithdrawn}
              </p>
              <p className="text-[1.0625rem] leading-[1.375rem] text-hint">
                BTN
              </p>
            </div>
            <p className="text-[0.9375rem] leading-[1.25rem] text-subtitle-text">
              {t("mydeposits-page.total_withdrawn")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Statistics;
